import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { Card } from '@kingspan/design-system-components';
import { PagedResponse } from 'types/domainTypes';
import { StatusLabel } from './StatusLabel';

export function InlineLabel({ label, value }: { label: string; value?: string }): ReactElement | null {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!value) {
    return null;
  }

  return (
    <Grid container spacing={1} wrap="nowrap">
      <Grid item>
        <Typography variant="body2" color={theme.palette.grey[500]} noWrap>
          {t(label)}
        </Typography>
      </Grid>
      <Grid item zeroMinWidth>
        <Typography variant="body2" color={theme.palette.common.black} noWrap textOverflow="ellipsis">
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
}

type Props = {
  pageData: PagedResponse;
};

export function ProjectStack({ pageData }: Props): ReactElement {
  const navigate = useNavigate();

  function openProject(id: string) {
    navigate(`/project/${id}`);
  }

  return (
    <Stack>
      <Grid container spacing={3} wrap="wrap" marginBottom="24px">
        {pageData.data.map((project) => (
          <Grid item xs={12} sm={6} xl={4} key={project.id} data-testid={'project-card'}>
            <Card cardTitle={project.name} handleClick={() => openProject(project.id)}>
              <Grid container direction="column" height={122} justifyContent="space-between" marginTop={1} wrap="nowrap">
                <Grid item zeroMinWidth sx={{ mt: 0.5 }}>
                  <Stack>
                    <InlineLabel
                      label="projects-page.project-card.label-created-by"
                      value={project.createdByName ? project.createdByName : project.createdBy}
                    />
                    <InlineLabel label="projects-page.project-card.label-project-engineer" value={project.engineer} />
                    <InlineLabel label="projects-page.project-card.label-client" value={project.client} />
                  </Stack>
                </Grid>
                <Grid item>
                  <StatusLabel size="small" value={project.status} />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
